import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Building2, Mail, Calendar, CheckCircle, XCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { ConnectHeader } from "@/components/ConnectHeader";
import { useTranslation } from "react-i18next";

interface ConnectionInvitation {
  id: string;
  inviter_org_id: string;
  invitee_org_id?: string;
  invited_by: string;
  invitation_type: string;
  invitation_target: string;
  connection_type: string;
  status: string;
  message?: string;
  expires_at: string;
  created_at: string;
  // Add inviter organization details
  inviter_org_name?: string;
  inviter_name?: string;
}

const Connect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  
  const [invitation, setInvitation] = useState<ConnectionInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [existingConnection, setExistingConnection] = useState<any>(null);
  const [isNotOrgMember, setIsNotOrgMember] = useState(false);

  const invitationId = searchParams.get("invitation");

  useEffect(() => {
    if (authLoading) return;

    // Always check for invitation ID first
    if (!invitationId) {
      toast({
        title: t("connect.invalid_invitation"),
        description: t("connect.no_invitation_id"),
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    // Always fetch invitation details first (whether logged in or not)
    fetchInvitation();
  }, [authLoading, invitationId, navigate]);

  const fetchInvitation = async () => {
    try {
      setLoading(true);

      // Fetch invitation details with organization info
      const { data: invitationData, error } = await supabase
        .from("connection_invitations")
        .select("*")
        .eq("id", invitationId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching invitation:", error);
        toast({
          title: t("connect.error_loading"),
          description: t("connect.problem_loading"),
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      if (!invitationData) {
        console.error("Invitation not found:", invitationId);
        toast({
          title: t("connect.invitation_not_found"),
          description: t("connect.invitation_expired_removed"),
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      // Don't redirect for expired invitations anymore - let the UI handle it
      // We want to show the invitation details even if it's expired

      // Removed email validation - users can accept invitations regardless of which email they're logged in with
      // If they have access to the invitation link, they should be able to accept it

      // Fetch organization name and inviter name separately
      const [orgResult, userResult] = await Promise.all([
        supabase
          .from("organizations")
          .select("name")
          .eq("id", invitationData.inviter_org_id)
          .maybeSingle(),
        supabase
          .from("user_profiles")
          .select("full_name, email")
          .eq("user_id", invitationData.invited_by)
          .maybeSingle()
      ]);

      // Get inviter name with better fallback logic
      let inviterName = "Team Member";
      if (userResult.data?.full_name) {
        inviterName = userResult.data.full_name;
      } else if (userResult.data?.email) {
        inviterName = userResult.data.email;
      }

      setInvitation({
        ...invitationData,
        inviter_org_name: orgResult.data?.name || "Organization",
        inviter_name: inviterName,
      });

      // If user is authenticated, check for existing connection and organization membership
      if (user) {
        await checkExistingConnection(invitationData.inviter_org_id);
        await checkOrganizationMembership(invitationData);
      }

    } catch (error) {
      console.error("Error:", error);
      toast({
        title: t("connect.error"),
        description: t("connect.failed_load_details"),
        variant: "destructive",
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const checkExistingConnection = async (inviterOrgId: string) => {
    if (!user) return;

    try {
      // Get current user's organization(s)
      const { data: userOrgs, error: userOrgError } = await supabase
        .from("organization_members")
        .select("org_id")
        .eq("user_id", user.id);

      if (userOrgError || !userOrgs || userOrgs.length === 0) {
        console.log("No user organizations found");
        return;
      }

      const userOrgIds = userOrgs.map(org => org.org_id);
      console.log("User org IDs:", userOrgIds);
      console.log("Inviter org ID:", inviterOrgId);
      console.log("Is self invitation?", userOrgIds.includes(inviterOrgId));
      
      // Check if user is trying to accept invitation from their own organization
      if (userOrgIds.includes(inviterOrgId)) {
        console.log("Self-invitation detected - user belongs to inviter org");
        setExistingConnection({
          id: 'self',
          status: 'active',
          inviter_org_id: inviterOrgId,
          invitee_org_id: userOrgIds[0],
          self_invitation: true
        });
        return;
      }
      
      // Check for existing connection using the normalized_pair_id approach
      // This will find connections in both directions (A->B and B->A)
      let existingConn = null;
      
      for (const userOrgId of userOrgIds) {
        // Generate the normalized pair ID using the same logic as the database
        const org1 = userOrgId;
        const org2 = inviterOrgId;
        const normalizedPairId = org1 < org2 ? `${org1}_${org2}` : `${org2}_${org1}`;
        
        console.log(`Checking normalized pair ID: ${normalizedPairId}`);
        
        const { data: conn, error: connError } = await supabase
          .from("connections")
          .select("*")
          .eq("normalized_pair_id", normalizedPairId);

        console.log("Connection query result:", conn);
        console.log("Connection query error:", connError);

        if (conn && conn.length > 0) {
          existingConn = conn[0];
          console.log("Found existing connection:", existingConn);
          break;
        }
      }

      if (existingConn) {
        console.log("Setting existing connection:", existingConn);
        setExistingConnection(existingConn);
      } else {
        console.log("No existing connection found");
      }
    } catch (error) {
      console.error("Error checking existing connection:", error);
    }
  };

  const checkOrganizationMembership = async (invitationData: any) => {
    if (!user || invitationData.invitation_type !== 'organization' || !invitationData.invitee_org_id) {
      setIsNotOrgMember(false);
      return;
    }

    try {
      const { data: membership, error: membershipError } = await supabase
        .from('organization_members')
        .select('id')
        .eq('org_id', invitationData.invitee_org_id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (membershipError) {
        console.error("Error checking organization membership:", membershipError);
        setIsNotOrgMember(false);
        return;
      }

      setIsNotOrgMember(!membership);
    } catch (error) {
      console.error("Error in checkOrganizationMembership:", error);
      setIsNotOrgMember(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!invitation || !user) return;

    try {
      setAccepting(true);

      // Additional validation for organization invitations
      if (invitation.invitation_type === 'organization') {
        // Check if the logged in user is a member of the intended organization
        const { data: membership, error: membershipError } = await supabase
          .from('organization_members')
          .select('id')
          .eq('org_id', invitation.invitee_org_id)
          .eq('user_id', user.id)
          .maybeSingle();

        if (membershipError) {
          console.error("Error checking organization membership:", membershipError);
          toast({
            title: t("connect.error"),
            description: t("connect.failed_verify_membership"),
            variant: "destructive",
          });
          return;
        }

        if (!membership) {
          toast({
            title: t("connect.access_denied"),
            description: t("connect.must_be_org_member"),
            variant: "destructive",
          });
          return;
        }
      }

      // Call the accept_connection_invitation function
      const { error } = await supabase.rpc("accept_connection_invitation", {
        invitation_id: invitation.id,
      });

      if (error) {
        console.error("Error accepting invitation:", error);
        toast({
          title: t("connect.error"),
          description: t("connect.failed_accept"),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("connect.invitation_accepted"),
        description: t("connect.now_connected", { orgName: invitation.inviter_org_name }),
      });

      // Redirect to dashboard or appropriate page
      navigate("/");

    } catch (error) {
      console.error("Error:", error);
      toast({
        title: t("connect.error"),
        description: t("connect.unexpected_error"),
        variant: "destructive",
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleRejectInvitation = async () => {
    if (!invitation) return;

    try {
      const { error } = await supabase
        .from("connection_invitations")
        .update({ 
          status: "rejected",
          updated_at: new Date().toISOString()
        })
        .eq("id", invitation.id);

      if (error) {
        console.error("Error rejecting invitation:", error);
        toast({
          title: t("connect.error"),
          description: t("connect.failed_reject"),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("connect.invitation_rejected"),
        description: t("connect.declined_invitation"),
      });

      navigate("/");
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: t("connect.error"),
        description: t("connect.unexpected_error"),
        variant: "destructive",
      });
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t("connect.loading")}</p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated but we have invitation details
  if (!user && invitation) {
    const isExpired = new Date() > new Date(invitation.expires_at);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <ConnectHeader />
        
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
                <Building2 className="h-8 w-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">{t("connect.youre_invited")}</CardTitle>
              <CardDescription>
                {invitation.inviter_org_name} {t("connect.wants_to_connect")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Invitation Details - Same style as authenticated version */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="text-center mb-4">
                  <h3 className="font-semibold text-gray-900 mb-3">{t("connect.partnership_invitation")}</h3>
                </div>
                
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-600" />
                  <span className="font-medium">{t("connect.from")}</span>
                  <span>{invitation.inviter_org_name}</span>
                </div>
                
                {invitation.inviter_name && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-600" />
                    <span className="font-medium">{t("connect.invited_by")}</span>
                    <span>{invitation.inviter_name}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <span className="font-medium">{t("connect.expires")}</span>
                  <span>{new Date(invitation.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                </div>
              </div>

              {/* Status Badges */}
              <div className="flex gap-2">
                <Badge variant={invitation.connection_type === "business" ? "default" : "secondary"}>
                  {invitation.connection_type} {t("connect.partnership")}
                </Badge>
                <Badge variant={isExpired ? "destructive" : "outline"}>
                  {isExpired ? t("connect.expired") : t("connect.active")}
                </Badge>
              </div>

              {/* Message */}
              {invitation.message && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">{invitation.message}</p>
                </div>
              )}

              {!isExpired ? (
                <>
                  <div className="text-center">
                    <p className="text-muted-foreground mb-4">
                      {t("connect.ready_to_join")}
                    </p>
                  </div>
                  
                  <Button 
                    onClick={() => {
                      const returnUrl = `/connect?invitation=${invitationId}`;
                      const userType = invitation.connection_type === 'carrier' ? 'carrier_company' : 'business_customer';
                      navigate(`/auth?returnUrl=${encodeURIComponent(returnUrl)}&userType=${userType}`);
                    }}
                    className="w-full text-white bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90 border-0"
                    size="lg"
                  >
                    {t("connect.create_account_signin")}
                  </Button>
                </>
              ) : (
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-red-800 mb-4">{t("connect.this_invitation_expired")}</p>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full"
                  >
                    {t("connect.return_to_home")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show generic login if no invitation details yet
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <ConnectHeader />
        
        <div className="max-w-md mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
                <Building2 className="h-8 w-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">{t("connect.loading_invitation")}</CardTitle>
              <CardDescription>
                {t("connect.loading_invitation_wait")}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show loading while fetching invitation
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t("connect.loading_invitation")}</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  const isExpired = new Date() > new Date(invitation.expires_at);
  // For link-type invitations, multiple users can accept the same invitation
  // Only check if it's processed for non-link invitations
  const isAlreadyProcessed = invitation.invitation_type !== "link" && invitation.status !== "active";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <ConnectHeader />
      
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">{t("connect.connection_invitation")}</CardTitle>
            <CardDescription>
              {t("connect.invited_connect_partner")}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Invitation Details */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-600" />
                <span className="font-medium">{t("connect.from")}</span>
                <span>{invitation.inviter_org_name}</span>
              </div>
              
              {invitation.inviter_name && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-600" />
                  <span className="font-medium">{t("connect.invited_by")}</span>
                  <span>{invitation.inviter_name}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-600" />
                <span className="font-medium">{t("connect.expires")}</span>
                <span>{new Date(invitation.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
              </div>
            </div>

            {/* Status Badges */}
            <div className="flex gap-2">
              <Badge variant={invitation.connection_type === "business" ? "default" : "secondary"}>
                {invitation.connection_type === "carrier" ? (
                  i18n.language === "ar" ? "خدمات نقل" : "Carrier Partnership"
                ) : (
                  `${invitation.connection_type} ${t("connect.partnership")}`
                )}
              </Badge>
              <Badge variant={isExpired ? "destructive" : "outline"}>
                {isExpired ? t("connect.expired") : t("connect.active")}
              </Badge>
              {isAlreadyProcessed && (
                <Badge variant={invitation.status === "accepted" ? "default" : "destructive"}>
                  {invitation.status}
                </Badge>
              )}
            </div>

            {/* Message */}
            {invitation.message && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">{invitation.message}</p>
              </div>
            )}

            {/* Organization membership warning */}
            {isNotOrgMember && invitation.invitation_type === 'organization' && (
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center justify-center mb-2">
                  <XCircle className="h-5 w-5 text-orange-600 mr-2" />
                  <span className="font-semibold text-orange-800">{t("connect.organization_access_required")}</span>
                </div>
                <p className="text-orange-700">
                  {t("connect.must_be_member")}
                </p>
              </div>
            )}

            {/* 1. Check if user is already connected - Show Green Component */}
            {existingConnection?.status === 'active' && !existingConnection.self_invitation ? (
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  <span className="font-semibold text-green-800">{t("connect.already_connected")}</span>
                </div>
                <p className="text-green-700">
                  {t("connect.already_have_connection", { status: existingConnection.status, orgName: invitation.inviter_org_name })}
                </p>
              </div>
            ) : 
            /* 2. If not connected, check if expired - Show Red Component */
            (isExpired || invitation.status !== 'active') ? (
              <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center justify-center mb-2">
                  <XCircle className="h-5 w-5 text-red-600 mr-2" />
                  <span className="font-semibold text-red-800">
                    {isExpired ? t("connect.invitation_expired") : t("connect.invitation_not_active")}
                  </span>
                </div>
                <p className="text-red-700">
                  {isExpired 
                    ? t("connect.invitation_expired_on", { 
                        orgName: invitation.inviter_org_name, 
                        date: new Date(invitation.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      })
                    : t("connect.invitation_no_longer_active", { orgName: invitation.inviter_org_name })
                  }
                </p>
              </div>
            ) : 
            /* 3. If self invitation - Show Blue Component */
            existingConnection?.self_invitation ? (
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="font-semibold text-blue-800">{t("connect.invitation")}</span>
                </div>
                <p className="text-blue-700 mb-3">
                  {t("connect.how_users_see_invitation")}
                </p>
                <Button 
                  onClick={async () => {
                    try {
                      const inviteLink = `${window.location.origin}/connect?invitation=${invitation.id}`;
                      await navigator.clipboard.writeText(inviteLink);
                      toast({
                        title: t("connect.link_copied"),
                        description: t("connect.link_copied_description"),
                      });
                    } catch (error) {
                      toast({
                        title: t("connect.failed_to_copy"),
                        description: t("connect.copy_manually"),
                        variant: "destructive",
                      });
                    }
                  }}
                  className="text-white bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90 border-0"
                >
                  {t("connect.share_with_others")}
                </Button>
              </div>
            ) : 
            /* 4. If no active connection & invitation is active & user has permission - Show Accept/Decline Buttons */
            (invitation.invitation_type !== 'organization' || !isNotOrgMember) ? (
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleAcceptInvitation}
                  disabled={accepting}
                  className="flex-1 text-white bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90 border-0"
                >
                  {accepting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {t("connect.accept_invitation")}
                </Button>
                
                <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex-1">
                      <XCircle className="h-4 w-4 mr-2" />
                      {t("connect.decline")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t("connect.decline_partnership")}</DialogTitle>
                      <DialogDescription>
                        {t("connect.decline_confirmation", { orgName: invitation.inviter_org_name })}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowDeclineDialog(false)}>
                        {t("connect.cancel")}
                      </Button>
                      <Button
                        onClick={() => {
                          handleRejectInvitation();
                          setShowDeclineDialog(false);
                        }}
                        variant="destructive"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        {t("connect.decline_invitation")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            ) : null
            }


            {/* Status Messages */}
            {isExpired && (
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-red-800">{t("connect.this_invitation_expired")}</p>
              </div>
            )}

            {isAlreadyProcessed && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-800">
                  {t("connect.already_processed", { status: invitation.status })}
                </p>
              </div>
            )}

            {/* Navigation - Always visible */}
            <div className="text-center pt-4">
              <Button variant="outline" onClick={() => navigate("/")}>
                {t("language.arabic") === "العربية" && i18n.language === "ar" ? (
                  <ArrowRight className="h-4 w-4 mr-2" />
                ) : (
                  <ArrowLeft className="h-4 w-4 mr-2" />
                )}
                {t("connect.return_to_dashboard")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Connect;